"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Datasheet = void 0;
const core_1 = require("../core");
const core_2 = require("@apitable/core");
const view_1 = require("./view");
const field_1 = require("./field");
const record_1 = require("./record");
const model_1 = require("../model");
const store_1 = require("../store");
/**
 * Datasheet operations
 *
 * - {@link id} The unique ID of the datasheet.
 *
 * - {@link name} The name of the datasheet.
 *
 * - {@link url} The url of the datasheet.
 *
 * - {@link description} The description of the Datasheet.
 *
 * - {@link views}: All views of the datasheet.
 *
 * - {@link fields}: All fields of the datasheet.
 *
 * - {@link getView}: Get the specified view in the datasheet.
 *
 * - {@link getField}: Get the specified field in the datasheet.
 *
 * - {@link createFieldAsync}: Create a new field.
 *
 * - {@link deleteFieldAsync}: Delete the specified field.
 *
 * - {@link getRecordAsync}: Get the record by record ID in the datasheet.
 *
 * - {@link getRecordsAsync}: Batch get records in the datasheet.
 *
 * - {@link createRecordAsync}: Create a new record with the specified cell values.
 *
 * - {@link createRecordsAsync}: Create new records with the specified cell values.
 *
 * - {@link updateRecordAsync}: Updates cell values for a record.
 *
 * - {@link updateRecordsAsync}: Updates cell values for records.
 *
 * - {@link deleteRecordAsync}: Delete the specified record.
 *
 * - {@link deleteRecordsAsync}: Delete the specified records.
 *
 */
class Datasheet {
    /**
     * @inner
     * @hidden
     */
    constructor(datasheetId, wCtx) {
        var _a;
        this.datasheetId = datasheetId;
        this.wCtx = wCtx;
        this.modalDatasheet = new model_1.Datasheet(datasheetId, wCtx);
        this.datasheetData = (_a = wCtx.widgetStore) === null || _a === void 0 ? void 0 : _a.getState().datasheetMap[this.datasheetId];
    }
    /**
     * The unique ID of the datasheet.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.id);
     * // => 'dstxxxxxxx'
     * ```
     */
    get id() {
        var _a, _b;
        return (_b = (_a = this.datasheetData) === null || _a === void 0 ? void 0 : _a.datasheet) === null || _b === void 0 ? void 0 : _b.datasheetId;
    }
    /**
     * The name of the datasheet.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.name);
     * // => 'datasheetName'
     * ```
     */
    get name() {
        var _a, _b;
        return (_b = (_a = this.datasheetData) === null || _a === void 0 ? void 0 : _a.datasheet) === null || _b === void 0 ? void 0 : _b.datasheetName;
    }
    /**
     * The description of the datasheet.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.description);
     * // => 'description information'
     * ```
     */
    get description() {
        var _a, _b;
        return (_b = (_a = this.datasheetData) === null || _a === void 0 ? void 0 : _a.datasheet) === null || _b === void 0 ? void 0 : _b.description;
    }
    /**
     * The url of the datasheet.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.url);
     * ```
     */
    get url() {
        return `${window.location.origin}/workbench/${this.datasheetId}`;
    }
    /**
     * All views of the datasheet.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.views);
     * // => [View, View]
     * ```
     */
    get views() {
        const state = this.wCtx.widgetStore.getState();
        const viewList = core_1.Selectors.getViewsList(state, this.datasheetId);
        return viewList.map(view => new view_1.View(this.datasheetId, this.wCtx, view));
    }
    /**
     * All fields of the datasheet.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.fields);
     * // => [Field, Field]
     * ```
     */
    get fields() {
        var _a;
        const state = this.wCtx.widgetStore.getState();
        const currentView = core_1.Selectors.getCurrentView(state);
        const fieldDataMap = core_1.Selectors.getFieldMap(state, this.datasheetId);
        const finalView = currentView || core_1.Selectors.getViewsList(state, this.datasheetId)[0];
        return (_a = (finalView.columns || [])) === null || _a === void 0 ? void 0 : _a.map(({ fieldId }) => {
            return new field_1.Field(this.datasheetId, this.wCtx, fieldDataMap[fieldId]);
        });
    }
    /**
     * Get the specified view in the datasheet.
     *
     * @param viewKey Name or ID of the view
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.getView('viewName')); // => View
     * console.log(myDatasheet.getView('viwxxxxxx')); // => View
     * ```
     */
    getView(viewKey) {
        const view = this.getViewByIdOrNameIfExists(viewKey);
        if (!view) {
            throw new Error('No view with ID in datasheet');
        }
        return view;
    }
    getViewByIdOrNameIfExists(viewKey) {
        const state = this.wCtx.widgetStore.getState();
        const views = core_1.Selectors.getViewsList(state, this.datasheetId);
        const finalView = views.find(({ id, name }) => [id, name].includes(viewKey));
        if (!finalView) {
            return null;
        }
        return new view_1.View(this.datasheetId, this.wCtx, finalView);
    }
    /**
     * Get the specified field in the datasheet.
     *
     * @param fieldKey Name or ID of the field.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.getField('fieldName')); // => Field
     * console.log(myDatasheet.getField('fldxxxxxx')); // => Field
     * ```
     */
    getField(fieldKey) {
        const state = this.wCtx.widgetStore.getState();
        const fieldDataMap = core_1.Selectors.getFieldMap(state, this.datasheetId);
        let fieldId = fieldKey;
        if (!fieldDataMap[fieldId]) {
            const field = Object.values(fieldDataMap).find(({ name }) => fieldKey === name);
            if (field != null) {
                fieldId = field.id;
            }
        }
        const field = core_1.Selectors.getField(state, fieldId, this.datasheetId);
        return new field_1.Field(this.datasheetId, this.wCtx, field);
    }
    /**
     * Create a new field.
     *
     * @param name The name of the field.
     * @param type The type of the field.
     * @param property The property of the field.
     *
     * @returns
     *
     * #### Description
     *
     * Refer to {@link FieldType} for supported field types, the write format for property, and other specifics for certain field types.
     *
     * #### Example
     * ```js
     * function createFieldAsync(name, type, property) {
     *   await datasheet.createFieldAsync(recordIds);
     * }
     * ```
     */
    createFieldAsync(name, type, property) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.modalDatasheet.addField(name, type, property);
        });
    }
    /**
     * Delete the specified field.
     *
     * @param fieldId The ID of the field.
     * @param conversion
     * When deleting a field as an associated field,
     * mark whether the associated field of the associated datasheet is deleted or converted to text, the default is Converted to a text field.
     *
     * @returns
     *
     * #### Description
     *
     * Throws an error if the user does not have permission to delete a field.
     *
     * #### Example
     * ```js
     * function deleteFieldAsync(fieldId) {
     *   await datasheet.deleteFieldAsync(fieldId);
     * }
     * ```
     */
    deleteFieldAsync(fieldId, conversion) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.modalDatasheet.deleteField(fieldId, conversion);
        });
    }
    /**
     * Get the record by record ID in the datasheet.
     *
     * @param recordId the ID of the record.
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.getRecordAsync(myRecordId));
     * // => Record
     * ```
     */
    getRecordAsync(recordId) {
        return new record_1.Record(this.datasheetId, this.wCtx, recordId);
    }
    /**
     * Batch get records in the datasheet.
     *
     * @param options.recordIds List of record IDs
     *
     * @param options.sorts Which fields the records need to be sorted by.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myDatasheet.getRecordsAsync()); // => [Record, Record, ...]
     * console.log(myDatasheet.getRecordsAsync({ recordIds: [myRecordId01, myRecordId02] })); // => [Record, Record]
     * ```
     */
    getRecordsAsync(options) {
        var _a;
        const state = this.wCtx.widgetStore.getState();
        const currentView = core_1.Selectors.getCurrentView(state, this.datasheetId);
        const finalView = currentView || core_1.Selectors.getViewsList(state, this.datasheetId)[0];
        const viewDerivate = core_2.ViewDerivateFactory.createViewDerivate(state, this.datasheetId, core_2.ViewType.Grid);
        const viewDerivation = viewDerivate.getViewDerivation(finalView);
        let finalRows;
        if (options == null || !((_a = options.recordIds) === null || _a === void 0 ? void 0 : _a.length)) {
            finalRows = viewDerivation.visibleRows;
        }
        else {
            const { recordIds } = options;
            finalRows = recordIds === null || recordIds === void 0 ? void 0 : recordIds.map(recordId => ({ recordId }));
        }
        const sorts = options === null || options === void 0 ? void 0 : options.sorts;
        if (sorts === null || sorts === void 0 ? void 0 : sorts.length) {
            const snapshot = (0, store_1.getSnapshot)(state, this.datasheetId);
            finalRows = core_1.Selectors.sortRowsBySortInfo(state, finalRows, sorts, snapshot);
        }
        return finalRows.map(({ recordId }) => {
            return new record_1.Record(this.datasheetId, this.wCtx, recordId);
        });
    }
    /**
     * Create a new record with the specified cell values.
     *
     * @param valuesMap object mapping fieldId to value for that field.
     *
     * @param insertPosition Position to insert in the view.
     *
     * @returns The returned promise will resolve to the recordId of the new record once it is persisted.
     *
     * #### Description
     * Adds a new record and optionally specifies its position in the view (default at the end), returning an array of new record IDs.
     *
     * An error will be thrown when the user does not have permission to perform the operation or when the cell value format check does not pass.
     *
     * Refer to {@link FieldType} for cell value write formats.
     *
     * #### Example
     * ```js
     * async function createNewRecord(valuesMap) {
     *   const newRecordId = await datasheet.createRecordAsync(valuesMap);
     *   alert(`The newly created record ID is ${newRecordId}`);
     * }
     *
     * // The key of the parameter is the fieldId and the value is the cell value.
     * createNewRecord({
     *   fld1234567980: 'this is a text value',
     *   fld0987654321: 1024,
     * });
     *
     * // Different types of field cell values have specific data structures that need to be passed in correctly
     * createNewRecord({
     *   fld1234567890: 'this is a text value', // SingleLineText
     *   fld0987654321: 1024, // Number
     *   fld1234567891: 'option 1', // SingleSelect
     *   fld1234567892: ['option 1', 'option 2'], // MultiSelect
     *   fld1234567893:  1635513510962, // DateTime
     *   fld1234567894: ['rec1234567'], // MagicLink
     * });
     * ```
     */
    createRecordAsync(valuesMap = {}, insertPosition) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.createRecordsAsync([valuesMap], insertPosition))[0];
        });
    }
    /**
     * Create new records with the specified cell values.
     *
     * @param records Array of objects with a fields key mapping fieldId to value for that field.
     * @param insertPosition Position to insert in the view.
     *
     * @returns The returned promise will resolve to an array of recordIds of the new records once the new records are persisted.
     *
     * #### Description
     * Add multiple records and optionally specify its position in the view (inserted at the end by default).
     *
     * An error will be thrown when the user does not have permission to perform the operation or when the cell value format check does not pass.
     *
     * Refer to {@link FieldType} for cell value write formats.
     *
     * #### Example
     * ```js
     * const records = [
     *   // Cell values should generally have format matching the output
     *   {
     *     fld1234567890: 'this is a text value',
     *     fld0987654321: 1024,
     *   },
     *   // Specifying no fields will create a new record with no cell values set
     *   {},
     *   // Different types of field cell values have specific data structures that need to be passed in correctly
     *   {
     *     fld1234567890: 'Cat video 2', // SingleLineText
     *     fld0987654321: 1024, // Number
     *     fld1234567891: 'option 1', // SingleSelect
     *     fld1234567892: ['option 1', 'option 2'], // MultiSelect
     *     fld1234567893:  1635513510962, // DateTime
     *     fld1234567894: ['rec1234567'], // MagicLink
     *   },
     * ];
     *
     * async function createNewRecords() {
     *   const recordIds = await datasheet.createRecordsAsync(records);
     *   alert(`new records with IDs: ${recordIds}`);
     * }
     * ```
     */
    createRecordsAsync(records = [], insertPosition) {
        return __awaiter(this, void 0, void 0, function* () {
            const finalRecords = records.map(record => ({ valuesMap: record }));
            return yield this.modalDatasheet.addRecords(finalRecords, insertPosition);
        });
    }
    /**
     * Updates cell values for a record.
     *
     * @param recordId The ID of the record.
     *
     * @param valuesMap
     * key for fieldId, value for the contents of the cell object,
     * only need to pass to modify the value, do not need to modify the key value do not need to pass.
     * To empty a field, you need to pass key: null.
     * @return
     *
     * #### Description
     * Throws an error if the user does not have permission to update the given cell values in the record, or or recordId does not exist,
     * or when the written value type does not match.
     *
     * Refer to {@link FieldType} for cell value write formats.
     *
     * If you need to modify multiple records at the same time, use the {@link updateRecordsAsync}.
     *
     * #### Example
     * ```js
     * function updateRecordAsync(recordId, valuesMap) {
     *     datasheet.updateRecordAsync(recordId, valuesMap);
     * }
     * ```
     */
    updateRecordAsync(recordId, valuesMap = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.updateRecordsAsync([{ id: recordId, valuesMap }]);
        });
    }
    /**
     * Updates cell values for records.
     * @param records Specify the records be modified.
     * @return
     *
     * #### Description
     * Throws an error if the user does not have permission to update the given cell values in the record, or or recordId does not exist,
     * or when the written value type does not match.
     *
     * valuesMap key for fieldId, value for the contents of the cell object,
     * only need to pass to modify the value, do not need to modify the key value do not need to pass.
     * To empty a field, you need to pass key: null.
     *
     * We refer to a field in a record as a cell.
     * Refer to {@link FieldType} for cell value write formats.
     *
     * If you only need to modify a single record, use the {@link updateRecordAsync}
     *
     * #### Example
     * ```js
     * function updateRecordsAsync(id, valuesMap) {
     *   datasheet.updateRecordsAsync([{ id, valuesMap }]);
     * }
     * ```
     */
    updateRecordsAsync(records) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.modalDatasheet.setRecords(records);
        });
    }
    /**
     * Delete the specified record.
     *
     * @param recordId The ID of the record.
     * @returns
     *
     * #### Description
     * Delete a record by recordId.
     *
     * Throws an error if the user does not have permission to delete the specified record.
     *
     * #### Example
     * ```js
     * async function deleteRecordAsync(recordId) {
     *   await datasheet.deleteRecordAsync(recordId);
     *   alert('The record has been deleted');
     * }
     * ```
     */
    deleteRecordAsync(recordId) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.deleteRecordsAsync([recordId]);
        });
    }
    /**
     * Delete the specified records.
     *
     * @param recordIds Array of recordIds.
     * @returns
     *
     *
     * #### Description
     * Delete the given record by recordIds.
     *
     * Throws an error if the user does not have permission to delete the given record.
     *
     * #### Example
     * ```js
     * async function deleteRecordsAsync(recordIds) {
     *   await datasheet.deleteRecordsAsync(recordIds);
     *   alert('The records has been deleted');
     * }
     * ```
     */
    deleteRecordsAsync(recordIds) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.modalDatasheet.deleteRecords(recordIds);
        });
    }
}
exports.Datasheet = Datasheet;
